/**
 * Client for the level endpoints.
 *
 * Levels come from the game's own source — today its Unity repository — and the
 * credentials for that live on the server. Nothing here holds one: the browser
 * asks by base, and the server decides what it may see.
 */

import { postJson } from "./jobsApi";

/**
 * Every level the configured source offers: `[{ levelId, name }]`.
 *
 * `refresh` asks past the server's cache, for when a level has just been added
 * to the source and the listing would otherwise be a few minutes behind.
 * `game` names whose levels (`"searchIt"` when left out, or `"tessera"`).
 */
export function listLevels(baseID, { refresh, game } = {}) {
  return postJson("levelApi/listLevels", {
    baseID: String(baseID),
    ...(refresh ? { refresh: true } : {}),
    ...(game ? { game: String(game) } : {}),
  });
}

/**
 * One level, inlined.
 *
 * `format` is `"map"` (the level as the game describes it) or the playable's own
 * shape — `"searchIt"`, or `"tessera"` with `game: "tessera"` — already arranged
 * into that playable's assets and level record.
 */
export function getLevel(baseID, levelId, { format, game } = {}) {
  return postJson("levelApi/getLevel", {
    baseID: String(baseID),
    levelId: String(levelId),
    ...(format ? { format } : {}),
    ...(game ? { game: String(game) } : {}),
  });
}

/**
 * The games a playable could be built from, as the repository lists them:
 * `[{ path, name, studio, game }]`, where `game` names the playable we already
 * have for it. `refresh` asks past the server's cache.
 */
export function listGames(baseID, { refresh } = {}) {
  return postJson("levelApi/listGames", {
    baseID: String(baseID),
    ...(refresh ? { refresh: true } : {}),
  });
}

/**
 * One game's branches: `{ branches, defaultBranch }`. A port is cut from a
 * branch and every repository names its own default, so the caller shows that
 * one first rather than assuming `main`.
 */
export function listBranches(baseID, project, { refresh } = {}) {
  return postJson("levelApi/listBranches", {
    baseID: String(baseID),
    project: String(project),
    ...(refresh ? { refresh: true } : {}),
  });
}

/**
 * Any game's repository, for a toolbox whose game has no module on the server:
 * `repoTree` lists a folder, `repoFiles` returns file contents as base64 with
 * Git LFS already resolved. The token stays on the server — the browser names a
 * project and a path, and may only name a game the server already lists.
 */
export function repoTree(baseID, project, { path, ref, recursive } = {}) {
  return postJson("levelApi/repoTree", {
    baseID: String(baseID),
    project: String(project),
    ...(path ? { path: String(path) } : {}),
    ...(ref ? { ref: String(ref) } : {}),
    ...(recursive ? { recursive: true } : {}),
  });
}

export function repoFiles(baseID, project, paths, { ref } = {}) {
  return postJson("levelApi/repoFiles", {
    baseID: String(baseID),
    project: String(project),
    paths,
    ...(ref ? { ref: String(ref) } : {}),
  });
}

/**
 * The Albert CMS, where the games whose repositories hold no levels keep theirs.
 * Told apart by what is named: nothing → the games, a game → its tables, a table
 * → a page of its levels (or one of them, by `id`). The key stays on the server.
 */
export function albert(baseID, { game, table, id, limit, offset, refresh } = {}) {
  return postJson("levelApi/albert", {
    baseID: String(baseID),
    ...(game ? { game: String(game) } : {}),
    ...(table ? { table: String(table) } : {}),
    ...(id !== undefined && id !== null ? { id } : {}),
    ...(limit ? { limit } : {}),
    ...(offset ? { offset } : {}),
    ...(refresh ? { refresh: true } : {}),
  });
}
